import { sharedApi, getAuthToken } from "./sharedApi";

// Inline minimal backend voice types (avoids conflict with exported VoiceInfo from text/features)
interface BackendVoiceInfo {
  id: string;
  name: string;
  language: string;
  gender: string; // backend currently returns plain string
  tier?: string;
  isActive?: boolean;
}

interface BackendVoicesResponse {
  voices: BackendVoiceInfo[];
  voicesByLanguage: Record<string, BackendVoiceInfo[]>;
  totalAvailable: number;
  userTier: string;
  languages: string[];
}

// Legacy enriched voice shape (kept for UI). Backend provides base fields via Voices endpoints.
export interface VoiceWithTier {
  id: string; // backend id
  voiceId: string; // alias/slim id used in old UI (mirror id now)
  displayName: string; // derived: name (can be formatted later)
  language: string;
  locale?: string; // optional (not in shared API yet)
  gender: string;
  region?: string;
  tier: "BASIC" | "PREMIUM"; // mapped from optional backend tier (default BASIC)
  isActive: boolean; // default true if undefined
  description?: string; // optional marketing text
}

export interface VoicesResponse {
  voices: VoiceWithTier[];
  voicesByLanguage: Record<string, VoiceWithTier[]>;
  totalAvailable: number;
  userTier: "BASIC" | "PREMIUM";
  languages: string[];
}

export interface VoiceLanguageResponse {
  voices: VoiceWithTier[];
  language: string;
  totalAvailable: number;
  userTier: "BASIC" | "PREMIUM";
  basicCount: number;
  premiumCount: number;
}

export interface VoiceAccessResponse {
  voiceId: string;
  voice: VoiceWithTier;
  canAccess: boolean;
  reason: string;
  userTier: "BASIC" | "PREMIUM";
  requiredTier: "BASIC" | "PREMIUM";
}

class VoiceService {
  // Authorization handled within api adapters
  /**
   * Get all available voices for the current user based on their subscription
   */
  async getAllVoices(): Promise<VoicesResponse> {
    const token = getAuthToken();
    const response = (await sharedApi.voices.list(
      token,
    )) as unknown as BackendVoicesResponse;
    const voices: VoiceWithTier[] = response.voices.map((v) => {
      const voice = v as unknown as Record<string, unknown>;
      return {
        id: v.id,
        voiceId: v.id,
        displayName: v.name,
        language: v.language,
        gender: v.gender,
        locale: typeof voice.locale === "string" ? voice.locale : undefined,
        region: typeof voice.region === "string" ? voice.region : undefined,
        tier: v.tier === "PREMIUM" ? "PREMIUM" : "BASIC",
        isActive: v.isActive ?? true,
        description:
          typeof voice.description === "string" ? voice.description : undefined,
      };
    });
    const voicesByLanguage: Record<string, VoiceWithTier[]> = voices.reduce(
      (acc, v) => {
        (acc[v.language] = acc[v.language] || []).push(v);
        return acc;
      },
      {} as Record<string, VoiceWithTier[]>,
    );
    return {
      voices,
      voicesByLanguage,
      totalAvailable: voices.length,
      userTier: response.userTier === "PREMIUM" ? "PREMIUM" : "BASIC",
      languages: response.languages,
    };
  }

  /**
   * Get voices for a specific language
   */
  async getVoicesForLanguage(language: string): Promise<VoiceLanguageResponse> {
    const token = getAuthToken();
    const response = (await sharedApi.voices.listByLanguage(
      language,
      token,
    )) as unknown as { voices: BackendVoiceInfo[]; language: string };
    const mapped: VoiceWithTier[] = response.voices.map((v) => {
      const voice = v as unknown as Record<string, unknown>;
      return {
        id: v.id,
        voiceId: v.id,
        displayName: v.name,
        language: v.language,
        gender: v.gender,
        locale: typeof voice.locale === "string" ? voice.locale : undefined,
        region: typeof voice.region === "string" ? voice.region : undefined,
        tier: (typeof voice.tier === "string" &&
        (voice.tier === "PREMIUM" || voice.tier === "BASIC")
          ? voice.tier
          : "BASIC") as "BASIC" | "PREMIUM",
        isActive: typeof voice.isActive === "boolean" ? voice.isActive : true,
        description:
          typeof voice.description === "string" ? voice.description : undefined,
      };
    });
    const basicCount = mapped.filter((v) => v.tier === "BASIC").length;
    const premiumCount = mapped.filter((v) => v.tier === "PREMIUM").length;
    return {
      voices: mapped,
      language: response.language,
      totalAvailable: mapped.length,
      userTier: "BASIC",
      basicCount,
      premiumCount,
    };
  }

  /**
   * Check if user can access a specific voice
   */
  async checkVoiceAccess(voiceId: string): Promise<VoiceAccessResponse> {
    const token = getAuthToken();
    const response = (await sharedApi.voices.check(
      voiceId,
      token,
    )) as unknown as {
      voiceId: string;
      voice: BackendVoiceInfo;
      canAccess: boolean;
      reason: string;
      userTier: string;
      requiredTier: string;
    };
    // Map backend voice to enriched shape for consistency
    const mappedVoice: VoiceWithTier = {
      id: response.voice.id,
      voiceId: response.voice.id,
      displayName: response.voice.name,
      language: response.voice.language,
      gender: response.voice.gender,
      tier: response.voice.tier === "PREMIUM" ? "PREMIUM" : "BASIC",
      isActive: response.voice.isActive ?? true,
    };
    return {
      voiceId: response.voiceId,
      voice: mappedVoice,
      canAccess: response.canAccess,
      reason: response.reason,
      userTier: response.userTier === "PREMIUM" ? "PREMIUM" : "BASIC",
      requiredTier: response.requiredTier === "PREMIUM" ? "PREMIUM" : "BASIC",
    };
  }

  /**
   * Check if voice is premium and user needs to upgrade
   */
  isPremiumVoiceBlocked(
    voice: VoiceWithTier,
    userTier: "BASIC" | "PREMIUM",
  ): boolean {
    return voice.tier === "PREMIUM" && userTier === "BASIC";
  }

  /**
   * Get display text for voice tier
   */
  getTierDisplayText(tier: "BASIC" | "PREMIUM"): string {
    return tier === "PREMIUM" ? "Premium" : "Free";
  }

  /**
   * Get tier badge color for UI
   */
  getTierBadgeColor(
    tier: "BASIC" | "PREMIUM",
  ): "default" | "warning" | "success" | "error" {
    return tier === "PREMIUM" ? "warning" : "default";
  }

  /**
   * Sort voices by tier and name for consistent display
   */
  sortVoices(voices: VoiceWithTier[]): VoiceWithTier[] {
    return voices.sort((a, b) => {
      // First sort by tier (BASIC first)
      if (a.tier !== b.tier) {
        return a.tier === "BASIC" ? -1 : 1;
      }
      // Then sort by display name
      return a.displayName.localeCompare(b.displayName);
    });
  }

  /**
   * Group voices by tier for organized display
   */
  groupVoicesByTier(voices: VoiceWithTier[]): {
    basic: VoiceWithTier[];
    premium: VoiceWithTier[];
  } {
    return {
      basic: voices.filter((v) => v.tier === "BASIC"),
      premium: voices.filter((v) => v.tier === "PREMIUM"),
    };
  }

  /**
   * Format voice display name with region info
   */
  formatVoiceDisplayName(voice: VoiceWithTier): string {
    return voice.region
      ? `${voice.displayName} (${voice.region})`
      : voice.displayName;
  }

  /**
   * Get voice description with tier info
   */
  getVoiceDescription(voice: VoiceWithTier): string {
    const tierText = this.getTierDisplayText(voice.tier);
    return voice.description ? `${voice.description} • ${tierText}` : tierText;
  }
}

export const voiceService = new VoiceService();
